// src/pages/auth/LinkCallback.jsx
//
// Lands here after the user clicks the magic link in their email.
// Validates the link, signs them in, and redirects. The user only
// sees this screen for a second or two while the SDK round-trips.
'use client';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth, db } from '@/firebase/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import AuthShell, {
  AuthPill,
  AuthHeading,
  AuthSubtitle,
  AuthButton,
} from '@/components/auth/AuthShell';

export default function LinkCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;

    const verifySignInLink = async () => {
      // Magic link key — MUST match what MagicLinkLogin.jsx writes
      // (it uses 'magicLinkEmail'). The pre-May-2026 code read
      // 'signInEmail' here, which silently broke every magic link
      // sign-in: the email was stored under one key and read under
      // another, so this branch always fell through to the "Missing
      // sign-in email" error toast and bounced the user back to the
      // password login screen. Bug fixed May 8, 2026.
      const email = window.localStorage.getItem('magicLinkEmail');
      if (!email) {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg('Missing sign-in email. Please restart the sign-in process.');
        }
        return;
      }

      try {
        if (isSignInWithEmailLink(auth, window.location.href)) {
          const result = await signInWithEmailLink(auth, email, window.location.href);
          const { user } = result;

          const userDoc = doc(db, 'users', user.uid);
          const snapshot = await getDoc(userDoc);

          if (!snapshot.exists()) {
            await setDoc(userDoc, {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || '',
              createdAt: serverTimestamp(),
              onboardingStatus: 'registered',
              role: 'pending',
              emailVerified: true,
              phoneVerified: false,
              teamId: null,
            });
          }

          if (cancelled) return;
          toast.success('Welcome back!');
          navigate('/dashboard');
        } else {
          if (!cancelled) {
            setStatus('error');
            setErrorMsg('Invalid sign-in link. Please request a new one.');
          }
        }
      } catch (err) {
        console.error('[LinkCallback] Error:', err);
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(err?.message || 'Sign-in failed. Please try again.');
        }
      } finally {
        // Always clear the stash so a stale email can't accidentally
        // be reused on a later sign-in attempt.
        try { window.localStorage.removeItem('magicLinkEmail'); } catch {}
      }
    };

    verifySignInLink();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <AuthShell cardWidth="narrow">
      <div className="text-center">
        {status === 'verifying' ? (
          <>
            <AuthPill>Signing you in</AuthPill>
            <div className="mx-auto mt-6 mb-2 h-12 w-12">
              <svg
                className="h-12 w-12 animate-spin text-[#b5f03d]"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.2" />
                <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
            </div>
            <AuthHeading size="small">
              Verifying your <span className="text-[#b5f03d]">link…</span>
            </AuthHeading>
            <AuthSubtitle>
              Just a moment — we're checking your sign-in link and dropping you on your dashboard.
            </AuthSubtitle>
          </>
        ) : (
          <>
            <AuthPill>Link issue</AuthPill>
            <AuthHeading size="small">
              That link <span className="text-[#b5f03d]">didn't work.</span>
            </AuthHeading>
            <AuthSubtitle>{errorMsg}</AuthSubtitle>
            <div className="mt-7">
              <AuthButton type="button" onClick={() => navigate('/login')}>
                Back to sign in
              </AuthButton>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}
